import React from "react";
import { PillLauncher, ConsultPanel, seedTranscript } from "./consult-panel.jsx";
import { NX_API as consultClient, NX_CONSULT_POINTER_KEY, NX_CONSULT_UNREAD_KEY } from "./consult-client.js";
import { createConsultPresentation, reduceConsultPresentation } from "./consult-presentation.js";
// Site-wide floating AI Consultation Assistant.
// Renders a position:fixed corner host into which PillLauncher and
// ConsultPanel (defined in consult/panel.jsx) drop. Tooltip is
// surfaced once per visitor; dismissal persists in localStorage.

const { useState: useState_cf, useEffect: useEffect_cf, useRef: useRef_cf, useCallback: useCallback_cf } = React;

const TIP_KEY = "nx.consult.tipSeen";

function ConsultFloating() {
  const [presentation, setPresentation] = useState_cf(createConsultPresentation);
  const [panelMounted, setPanelMounted] = useState_cf(false);
  const [closing, setClosing] = useState_cf(false);
  const [showTip, setShowTip] = useState_cf(false);
  const [draftRequest, setDraftRequest] = useState_cf(null);
  const [consultationState, setConsultationState] = useState_cf(() => seedTranscript("greet"));
  const [restoring, setRestoring] = useState_cf(() => Boolean(consultClient.loadPointer()));
  const [restoreError, setRestoreError] = useState_cf(false);
  const [expired, setExpired] = useState_cf(false);
  const [missingNotice, setMissingNotice] = useState_cf(false);
  const [consultationKey, setConsultationKey] = useState_cf(0);
  const [lifecycleVersion, setLifecycleVersion] = useState_cf(0);
  const requestIdRef = useRef_cf(0);
  const returnFocusRef = useRef_cf(null);
  const closeTimerRef = useRef_cf(null);
  const restoreGenerationRef = useRef_cf(0);
  const open = presentation.visible;
  const openRef = useRef_cf(open);
  openRef.current = open;

  const updatePresentation = useCallback_cf((type, details = {}) => {
    setPresentation((current) => reduceConsultPresentation(current, { type, ...details }));
  }, []);

  const publishUnread = useCallback_cf((unread) => {
    try {
      window.localStorage.setItem(NX_CONSULT_UNREAD_KEY, JSON.stringify({
        unread,
        eventId: `${Date.now()}-${Math.random()}`,
      }));
    } catch {}
  }, []);

  useEffect_cf(() => {
    const onStorage = async (event) => {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key === NX_CONSULT_UNREAD_KEY) {
        try {
          const signal = JSON.parse(event.newValue || "null");
          if (typeof signal?.unread === "boolean") {
            if (signal.unread && openRef.current) publishUnread(false);
            updatePresentation(signal.unread ? "remote-consultant-complete" : "remote-output-viewed");
          }
        } catch {}
        return;
      }
      if (event.key !== NX_CONSULT_POINTER_KEY) return;
      const restoreGeneration = ++restoreGenerationRef.current;
      const pointer = consultClient.adoptPointer(event.newValue);
      setLifecycleVersion((version) => version + 1);
      setExpired(false);
      setRestoreError(false);
      setMissingNotice(false);
      if (!pointer) {
        setRestoring(false);
        setConsultationState(seedTranscript("greet"));
        return;
      }
      const restored = await consultClient.restoreConsultation(pointer, { clearMissing: false });
      if (restoreGeneration !== restoreGenerationRef.current) return;
      if (restored?.status === "restored") {
        setConsultationState({
          msgs: restored.messages,
          focus: restored.focus,
          stage: "discover",
        });
      } else if (restored?.status === "temporary-error") {
        setRestoreError(true);
      } else if (restored?.status !== "missing") {
        setConsultationState(seedTranscript("greet"));
      }
      setRestoring(false);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [publishUnread, updatePresentation]);

  useEffect_cf(() => {
    if (!restoring) return undefined;
    let active = true;
    const restoreGeneration = ++restoreGenerationRef.current;
    consultClient.restoreConsultation().then((restored) => {
      if (!active || restoreGeneration !== restoreGenerationRef.current) return;
      if (restored?.status === "restored") {
        setConsultationState({
          msgs: restored.messages,
          focus: restored.focus,
          stage: "discover",
        });
        setRestoreError(false);
        setMissingNotice(false);
      } else if (restored?.status === "temporary-error") {
        setRestoreError(true);
        setMissingNotice(false);
      } else {
        setConsultationState(seedTranscript("greet"));
        setRestoreError(false);
        setMissingNotice(restored?.status === "missing");
      }
      setRestoring(false);
    });
    return () => { active = false; };
  }, [restoring]);

  useEffect_cf(() => {
    if (restoring || restoreError || expired || !consultClient.consultationId || !Number.isFinite(consultClient.expiresAt)) return undefined;
    const timer = window.setTimeout(() => {
      if (!consultClient.expireIfInactive()) return;
      setExpired(true);
      setLifecycleVersion((version) => version + 1);
    }, Math.max(0, consultClient.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [restoring, restoreError, expired, lifecycleVersion]);

  const updateConsultationState = useCallback_cf((state) => {
    setConsultationState(state);
  }, []);

  const handleComposerFocus = useCallback_cf(() => {
    updatePresentation("composer-focus", { viewportWidth: window.innerWidth });
  }, [updatePresentation]);

  const handleComposerBlur = useCallback_cf(({ draft, busy, formPending }) => {
    updatePresentation("composer-blur", {
      draft,
      busy,
      formPending,
      viewportWidth: window.innerWidth,
    });
  }, [updatePresentation]);

  const handleResponseComplete = useCallback_cf(() => {
    if (!openRef.current) publishUnread(true);
    updatePresentation("consultant-complete");
  }, [publishUnread, updatePresentation]);

  const startNew = useCallback_cf(() => {
    consultClient.startNewConsultation();
    setConsultationState(seedTranscript("greet"));
    setRestoreError(false);
    setMissingNotice(false);
    setExpired(false);
    setRestoring(false);
    setConsultationKey((key) => key + 1);
    setLifecycleVersion((version) => version + 1);
  }, []);

  const retryRestore = useCallback_cf(() => {
    setRestoreError(false);
    setRestoring(true);
  }, []);

  const recordLifecycleActivity = useCallback_cf((activity) => {
    const recorded = consultClient.recordActivity(activity);
    if (!recorded && consultClient.expired) setExpired(true);
    if (recorded || consultClient.expired) {
      setLifecycleVersion((version) => version + 1);
    }
  }, []);

  const syncSuccessfulTurn = useCallback_cf(() => {
    if (consultClient.expired) setExpired(true);
    setLifecycleVersion((version) => version + 1);
  }, []);

  useEffect_cf(() => {
    document.body.classList.toggle("nx-consult-open", open);
    return () => document.body.classList.remove("nx-consult-open");
  }, [open]);

  // First-visit tooltip — appears after a short beat so it doesn't fight
  // the hero entry animation, and only if the visitor hasn't seen it.
  useEffect_cf(() => {
    let seen = false;
    try { seen = localStorage.getItem(TIP_KEY) === "1"; } catch (_) {}
    if (seen) return;
    const t = setTimeout(() => setShowTip(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const dismissTip = () => {
    setShowTip(false);
    try { localStorage.setItem(TIP_KEY, "1"); } catch (_) {}
  };

  // Allow any page to open the singleton assistant. The optional event detail
  // is a draft-only path and stays separate from the legacy auto-send seed.
  useEffect_cf(() => {
    const onOpen = (event) => {
      const detail = event?.detail && typeof event.detail === "object" ? event.detail : {};
      const incomingDraft = typeof detail.draft === "string"
        ? detail.draft.trim().slice(0, 320)
        : "";

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (!open) {
        const activeElement = document.activeElement;
        returnFocusRef.current = activeElement && activeElement !== document.body
          ? activeElement
          : null;
      }

      setShowTip(false);
      try { localStorage.setItem(TIP_KEY, "1"); } catch (_) {}
      setDraftRequest({
        id: ++requestIdRef.current,
        draft: incomingDraft,
        focusComposer: Boolean(detail.focusComposer),
        source: typeof detail.source === "string" ? detail.source : "",
      });
      setClosing(false);
      recordLifecycleActivity("open");
      setPanelMounted(true);
      if (presentation.unread) publishUnread(false);
      updatePresentation("open");
    };
    window.addEventListener("nx:consult:open", onOpen);
    return () => window.removeEventListener("nx:consult:open", onOpen);
  }, [open, presentation.unread, publishUnread, recordLifecycleActivity, updatePresentation]);

  // Reverse the open animation before minimizing so the panel slides/fades
  // back out instead of vanishing. Presentation changes never end Consultation.
  const requestMinimize = () => {
    if (!closing) recordLifecycleActivity("minimize");
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === "function") activeElement.blur();
    updatePresentation("minimize");
    setClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setClosing(false);
      setDraftRequest(null);
      const returnTarget = returnFocusRef.current;
      returnFocusRef.current = null;
      const focusTarget = returnTarget?.isConnected
        ? returnTarget
        : document.querySelector(".nx-ai-launcher");
      if (focusTarget && typeof focusTarget.focus === "function") {
        focusTarget.focus({ preventScroll: true });
      }
    }, 300);
  };

  useEffect_cf(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape" || closing) return;
      event.preventDefault();
      requestMinimize();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closing]);

  useEffect_cf(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  const toggle = () => {
    dismissTip();
    if (open) requestMinimize();
    else {
      const activeElement = document.activeElement;
      returnFocusRef.current = activeElement && activeElement !== document.body
        ? activeElement
        : null;
      setDraftRequest(null);
      setClosing(false);
      recordLifecycleActivity("open");
      setPanelMounted(true);
      if (presentation.unread) publishUnread(false);
      updatePresentation("open");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      pointerEvents: "none",
      zIndex: open ? 96 : 60,
    }}>
      <PillLauncher
        open={open}
        onToggle={toggle}
        hasMessage={(showTip || presentation.unread) && !open}
        resumable={panelMounted}
      />
      {(open || closing) && (
        <div
          className={"nx-consult-scrim" + (closing ? " is-closing" : "")}
          onClick={requestMinimize}
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, pointerEvents: "auto" }}
        />
      )}
      {panelMounted && (restoring ? (
        <div
          className={"nx-consult-panel" + (closing ? " is-closing" : "") + (!open && !closing ? " is-minimized" : "")}
          role={open ? "dialog" : undefined}
          aria-modal={open ? "true" : undefined}
          aria-label="Restoring Consultation"
          aria-busy="true"
          aria-hidden={!open}
          inert={!open ? "" : undefined}
          style={{
            position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
            width: "min(468px, calc(100vw - 32px))",
            height: "min(640px, calc(100vh - 108px))",
            background: "#FFFCF6",
            borderRadius: 22,
            border: "1px solid var(--line)",
            boxShadow: "0 30px 72px -24px rgba(14,18,23,.34), 0 8px 20px -6px rgba(14,18,23,.14)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--fg-muted)", fontSize: 14,
            zIndex: 3, pointerEvents: open ? "auto" : "none",
          }}
        >
          Restoring your Consultation…
        </div>
      ) : restoreError ? (
        <div
          className={"nx-consult-panel" + (closing ? " is-closing" : "") + (!open && !closing ? " is-minimized" : "")}
          role={open ? "dialog" : undefined}
          aria-modal={open ? "true" : undefined}
          aria-label="Consultation restore failed"
          aria-hidden={!open}
          inert={!open ? "" : undefined}
          style={{
            position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)",
            width: "min(468px, calc(100vw - 32px))", minHeight: 260, padding: 28, boxSizing: "border-box",
            background: "#FFFCF6", borderRadius: 22, border: "1px solid var(--line)",
            boxShadow: "0 30px 72px -24px rgba(14,18,23,.34)", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--fg-muted)",
            zIndex: 3, pointerEvents: open ? "auto" : "none",
          }}
        >
          <strong style={{ color: "var(--fg)", marginBottom: 8 }}>We couldn’t restore your Consultation.</strong>
          <span>The saved Consultation is still available. Retry, or start a new one.</span>
          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button type="button" onClick={retryRestore}>Retry</button>
            <button type="button" onClick={startNew}>Start new</button>
          </div>
        </div>
      ) : (
        <ConsultPanel
          key={consultationKey}
          onMinimize={requestMinimize}
          closing={closing}
          minimized={!open && !closing}
          presentationMode={presentation.mode}
          draftRequest={draftRequest}
          consultationState={consultationState}
          onConsultationStateChange={updateConsultationState}
          onComposerFocus={handleComposerFocus}
          onComposerBlur={handleComposerBlur}
          onResponseComplete={handleResponseComplete}
          readOnly={expired}
          notice={missingNotice ? "We couldn’t find your saved Consultation, so a new one is ready." : ""}
          onStartNew={startNew}
          onSuccessfulSubmit={syncSuccessfulTurn}
          onExpired={() => setExpired(true)}
        />
      ))}
    </div>
  );
}

export { ConsultFloating };
